import { useCallback, useEffect, useRef } from 'react';
import { useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { STATUS_COLORS } from '../../constants/map';
import { getFeatureColor } from '../../utils/siteStatus';

const CanvasLayer = ({ sites, onSiteClick, colourMode, activityStatus, tileLayer, selectedFeatures, selectedTitles, hideInactive }) => {
  const canvasRef         = useRef(null);
  const sitesRef          = useRef(sites);
  const onClickRef        = useRef(onSiteClick);
  const colourRef         = useRef(colourMode);
  const actStatusRef      = useRef(activityStatus);
  const tileLayerRef      = useRef(tileLayer);
  const selectedFeatRef   = useRef(selectedFeatures);
  const selectedTitleRef  = useRef(selectedTitles || []);
  const hideInactiveRef   = useRef(hideInactive || false);
  const redrawRef         = useRef(null);

  useEffect(function() { onClickRef.current      = onSiteClick;       }, [onSiteClick]);
  useEffect(function() { colourRef.current       = colourMode;        }, [colourMode]);
  useEffect(function() { actStatusRef.current    = activityStatus;    }, [activityStatus]);
  useEffect(function() { tileLayerRef.current    = tileLayer;         }, [tileLayer]);
  useEffect(function() { selectedFeatRef.current  = selectedFeatures;         }, [selectedFeatures]);
  useEffect(function() { selectedTitleRef.current = selectedTitles || [];  }, [selectedTitles]);
  useEffect(function() {
    hideInactiveRef.current = hideInactive || false;
    if (redrawRef.current) redrawRef.current();
  }, [hideInactive]);

  const map = useMapEvents({
    moveend: function() { if (redrawRef.current) redrawRef.current(); },
    zoomend: function() { if (redrawRef.current) redrawRef.current(); },
    resize:  function() { if (redrawRef.current) redrawRef.current(); },
  });

  const redraw = useCallback(function() {
    var canvas = canvasRef.current;
    if (!canvas || !map) return;
    var size = map.getSize();
    canvas.width  = size.x;
    canvas.height = size.y;
    L.DomUtil.setPosition(canvas, map.containerPointToLayerPoint([0, 0]));
    var ctx       = canvas.getContext('2d');
    var zoom      = map.getZoom();
    var radius    = zoom >= 12 ? 5 : zoom >= 9 ? 3 : 2;
    var showLabel = zoom >= 12;
    var mode      = colourRef.current;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    var inactive = sitesRef.current.filter(function(s) { return s._status === 'none'; });
    var active   = sitesRef.current.filter(function(s) { return s._status !== 'none'; });
    inactive.concat(active).forEach(function(site) {
      var lat = parseFloat(site.latitude);
      var lng = parseFloat(site.longitude);
      if (isNaN(lat) || isNaN(lng)) return;
      try {
        var pt    = map.latLngToContainerPoint([lat, lng]);
        var color;
        var isFiltered    = false;
        var isClosed      = actStatusRef.current === 'Closed';
        var noneColor     = tileLayerRef.current === 'satellite' ? '#FFFFFF' : STATUS_COLORS.none;
        var featFilter    = selectedFeatRef.current;
        var hasFeatFilter  = featFilter && featFilter.length > 0;
        var titleFilter    = selectedTitleRef.current;
        var hasTitleFilter = titleFilter && titleFilter.length > 0;

        if (site._status === 'none') {
          color = noneColor;
        } else if (hasTitleFilter && !titleFilter.includes(site._title)) {
          color = noneColor;
          isFiltered = true;
        } else if (hasFeatFilter && !selectedFeatRef.current.includes(site._feature)) {
          color = noneColor;
          isFiltered = true;
        } else if (mode === 'feature') {
          color = getFeatureColor(site._feature);
        } else if (isClosed || site._status === 'overdue') {
          color = STATUS_COLORS.overdue || '#BF360C';
        } else {
          color = STATUS_COLORS[site._status] || STATUS_COLORS.none;
        }
        var hasActivity = site._status !== 'none' && !isFiltered;
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, radius, 0, Math.PI * 2);
        ctx.fillStyle   = color;
        ctx.globalAlpha = 0.9;
        ctx.fill();
        ctx.globalAlpha = 1;
        if (hasActivity) {
          ctx.strokeStyle = 'rgba(255,255,255,0.6)';
          ctx.lineWidth   = 1;
          ctx.stroke();
        }
        if (showLabel && site.site) {
          var tac   = site.tac != null ? site.tac : (site.tAC != null ? site.tAC : null);
          var label = tac ? site.site + ' (' + tac + ')' : site.site;
          ctx.font      = '700 9px IBM Plex Mono, monospace';
          ctx.textAlign = 'center';
          var tw = ctx.measureText(label).width;
          var lx = pt.x;
          var ly = pt.y + radius + 12;
          ctx.fillStyle = 'rgba(6,15,26,0.82)';
          ctx.fillRect(lx - tw / 2 - 3, ly - 9, tw + 6, 13);
          ctx.fillStyle = '#E0E7FF';
          ctx.fillText(label, lx, ly);
        }
      } catch (e) { void e; }
    });
  }, [map]);

  useEffect(function() { redrawRef.current = redraw; }, [redraw]);

  useEffect(function() {
    if (!map) return;
    var canvas = document.createElement('canvas');
    canvas.style.position      = 'absolute';
    canvas.style.pointerEvents = 'auto';
    canvas.style.cursor        = 'default';
    canvas.className           = 'leaflet-canvas-sites';
    map.getPane('overlayPane').appendChild(canvas);
    canvasRef.current = canvas;

    var handleClick = function(e) {
      var rect  = canvas.getBoundingClientRect();
      var cx    = e.clientX - rect.left;
      var cy    = e.clientY - rect.top;
      var zoom  = map.getZoom();
      var hitR  = zoom >= 12 ? 8 : zoom >= 9 ? 6 : 5;
      var best  = null;
      var bestD = hitR * hitR;
      sitesRef.current.forEach(function(site) {
        var lat = parseFloat(site.latitude);
        var lng = parseFloat(site.longitude);
        if (isNaN(lat) || isNaN(lng)) return;
        try {
          var pt = map.latLngToContainerPoint([lat, lng]);
          var d2 = (pt.x - cx) * (pt.x - cx) + (pt.y - cy) * (pt.y - cy);
          if (d2 < bestD) { bestD = d2; best = site; }
        } catch (e) { void e; }
      });
      if (best && onClickRef.current) {
        L.DomEvent.stopPropagation(e);
        onClickRef.current(best);
      }
    };

    var onMapEvent = function() { if (redrawRef.current) redrawRef.current(); };
    canvas.addEventListener('click', handleClick);
    map.on('moveend zoomend resize', onMapEvent);
    if (redrawRef.current) redrawRef.current();
    return function() {
      canvas.removeEventListener('click', handleClick);
      map.off('moveend zoomend resize', onMapEvent);
      canvas.remove();
      canvasRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  useEffect(function() {
    sitesRef.current        = sites;
    selectedFeatRef.current = selectedFeatures;
    selectedTitleRef.current = selectedTitles || [];
    redraw();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sites, JSON.stringify(selectedFeatures), JSON.stringify(selectedTitles), redraw]);

  useEffect(function() {
    colourRef.current = colourMode;
    redraw();
  }, [colourMode, redraw]);

  useEffect(function() {
    actStatusRef.current = activityStatus;
    redraw();
  }, [activityStatus, redraw]);

  useEffect(function() {
    tileLayerRef.current = tileLayer;
    redraw();
  }, [tileLayer, redraw]);

  return null;
};

export default CanvasLayer;